import axios from "axios";
import { isCookieRefreshModeEnabled } from "./auth-config.service";
import { refreshAccessSession } from "./auth-session.service";
import { clearAuthTokens, getAccessToken, getApiRootUrl, hasRefreshToken } from "./token.service";
import { isPublicAuthPath } from "../utils/public-auth-routes";
import { hasSsoTokenQuery, isSsoRoute } from "../utils/sso-entry";

const AUTH_EXCLUDED_PATHS = [
  "/auth/token/issue",
  "/auth/token/refresh",
  "/auth/token/logout",
  "/login",
  "/vue/login",
  "/validate-token",
  "/password-forgot",
  "/password-reset",
  "/complete-invite-signup",
  "/invite-details",
  "/invite/",
  "/sso",
];

let interceptorsInstalled = false;
let refreshPromise = null;
let appRouter = null;
let appStore = null;

const resolveActivePath = () => {
  const routerPath = appRouter?.currentRoute?.value?.path || "";
  const browserPath =
    typeof window !== "undefined" ? window.location.pathname || "" : "";

  // During first paint on /sso (and other public auth pages), the router can
  // still report "/" while purge/bootstrap requests are in flight. Treat the
  // browser path as the source of truth so we do not race into /login.
  if (browserPath && (!routerPath || routerPath === "/")) {
    return browserPath;
  }

  return routerPath || browserPath;
};

const isPublicAuthRoute = () => {
  if (typeof window !== "undefined") {
    const browserPath = window.location.pathname || "";
    const browserSearch = window.location.search || "";

    if (isSsoRoute(browserPath) || hasSsoTokenQuery(browserSearch)) {
      return true;
    }
  }

  return isPublicAuthPath(resolveActivePath());
};

const requestTargetsExcludedPath = (url = "") => AUTH_EXCLUDED_PATHS.some((path) => url.includes(path));
const INVALID_BEARER_REGEX = /^Bearer\s+(null|undefined|)$/i;
const trimTrailingSlash = (value = "") => String(value).replace(/\/+$/, "");

const getRuntimeOrigin = () => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "http://localhost";
};

const safeParseUrl = (url, base) => {
  try {
    return new URL(url, base);
  } catch (error) {
    return null;
  }
};

const resolveApiRootUrl = () => {
  const apiRoot = trimTrailingSlash(getApiRootUrl() || "");
  if (!apiRoot) {
    return null;
  }
  return safeParseUrl(apiRoot, getRuntimeOrigin());
};

const resolveRequestUrl = (url, baseURL) => {
  const runtimeOrigin = getRuntimeOrigin();
  const normalizedUrl = String(url || "");
  const normalizedBaseUrl = trimTrailingSlash(baseURL || "");

  if (normalizedBaseUrl) {
    const parsedBaseUrl = safeParseUrl(normalizedBaseUrl, runtimeOrigin);
    if (parsedBaseUrl) {
      return safeParseUrl(normalizedUrl, parsedBaseUrl.href);
    }
  }

  return safeParseUrl(normalizedUrl, runtimeOrigin);
};

const isApiRequest = (url, baseURL) => {
  const requestUrl = resolveRequestUrl(url, baseURL);
  const apiRootUrl = resolveApiRootUrl();
  if (!requestUrl || !apiRootUrl) {
    return false;
  }

  if (requestUrl.origin !== apiRootUrl.origin) {
    return false;
  }

  const apiPath = trimTrailingSlash(apiRootUrl.pathname || "");
  const requestPath = trimTrailingSlash(requestUrl.pathname || "");
  if (!apiPath) {
    return true;
  }

  return requestPath === apiPath || requestPath.startsWith(`${apiPath}/`);
};

const readHeader = (headers, name) => {
  if (!headers) {
    return "";
  }

  if (typeof headers.get === "function") {
    return headers.get(name) || "";
  }

  return headers[name] || headers[name.toLowerCase()] || "";
};

const writeHeader = (headers, name, value) => {
  if (!headers) {
    return;
  }

  if (typeof headers.set === "function") {
    headers.set(name, value);
    return;
  }

  headers[name] = value;
};

const removeHeader = (headers, name) => {
  if (!headers) {
    return;
  }

  if (typeof headers.delete === "function") {
    headers.delete(name);
    return;
  }

  delete headers[name];
  delete headers[name.toLowerCase()];
};

const redirectToLoginIfNeeded = async () => {
  if (isPublicAuthRoute()) {
    return;
  }

  try {
    appStore?.commit?.("auth/isAuthenticated", false);
  } catch (error) {
    console.warn("[axios-auth] Failed to update auth state", error);
  }

  const currentRouteName = appRouter?.currentRoute?.value?.name;
  if (appRouter && currentRouteName !== "Login") {
    await appRouter.push({ name: "Login" });
  }
};

const hardFailSession = async () => {
  clearAuthTokens();
  await redirectToLoginIfNeeded();
};

export const installAxiosAuthInterceptors = ({ store, router } = {}) => {
  appStore = store || appStore;
  appRouter = router || appRouter;

  if (interceptorsInstalled) {
    return;
  }
  interceptorsInstalled = true;

  axios.interceptors.request.use(
    (config) => {
      const isApiCall = isApiRequest(config?.url, config?.baseURL);
      if (!isApiCall) {
        return config;
      }

      const token = getAccessToken();
      const headers = config.headers || {};
      const existingAuthorization = String(readHeader(headers, "Authorization") || "").trim();
      const hasInvalidBearer = INVALID_BEARER_REGEX.test(existingAuthorization);

      if (token) {
        if (!existingAuthorization || hasInvalidBearer) {
          writeHeader(headers, "Authorization", `Bearer ${token}`);
        }
      } else if (hasInvalidBearer) {
        removeHeader(headers, "Authorization");
      }

      config.headers = headers;
      return config;
    },
    (error) => Promise.reject(error)
  );

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error?.config;
      const status = error?.response?.status;

      if (!originalRequest || status !== 401) {
        return Promise.reject(error);
      }

      if (!isApiRequest(originalRequest.url, originalRequest.baseURL)) {
        return Promise.reject(error);
      }

      if (originalRequest.__isRetryRequest) {
        if (!isPublicAuthRoute()) {
          await hardFailSession();
        }
        return Promise.reject(error);
      }

      if (requestTargetsExcludedPath(originalRequest.url || "")) {
        return Promise.reject(error);
      }

      if (!isCookieRefreshModeEnabled() && !hasRefreshToken()) {
        if (!isPublicAuthRoute()) {
          await hardFailSession();
        }
        return Promise.reject(error);
      }

      if (!refreshPromise) {
        refreshPromise = refreshAccessSession()
          .catch(async (refreshError) => {
            if (!isPublicAuthRoute()) {
              await hardFailSession();
            }
            throw refreshError;
          })
          .finally(() => {
            refreshPromise = null;
          });
      }

      try {
        await refreshPromise;
        const nextToken = getAccessToken();
        if (!nextToken) {
          if (!isPublicAuthRoute()) {
            await hardFailSession();
          }
          return Promise.reject(error);
        }

        originalRequest.__isRetryRequest = true;
        originalRequest.headers = originalRequest.headers || {};
        writeHeader(originalRequest.headers, "Authorization", `Bearer ${nextToken}`);
        return axios(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }
  );
};
